<template>
  <div>
    <!-- Rental Button -->
    <button @click="handleClick"
      :class="[
        'flex flex-row-reverse md:flex-row items-center justify-between md:justify-center space-x-0  md:space-x-2 px-8 py-3 rounded-md transition-colors font-semibold',
        buttonClass || 'bg-yellow-500 text-black hover:bg-yellow-400'
      ]">
      <font-awesome-icon icon="credit-card" />
      <span class="hidden md:block">Play Without Ads (${{ rentalPrice }})</span>
      <span class="block md:hidden">Rent for ${{ rentalPrice }}</span>
    </button>

    <!-- Modal -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
      @click.self="closeModal">
      <div class="bg-white rounded-lg p-8 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <!-- Modal Header -->
        <div class="flex justify-between items-center mb-6">
          <h2 class="text-2xl font-bold text-gray-900">Rent Video</h2>
          <button @click="closeModal" class="text-gray-400 hover:text-gray-600 transition-colors">
            <font-awesome-icon icon="times" size="lg" />
          </button>
        </div>

        <!-- Video Info -->
        <div class="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 class="font-semibold text-gray-900">{{ videoTitle }}</h3>
          <p class="text-gray-600 text-sm mt-1">Rental Period: 72 hours</p>
          <p class="text-lg font-bold text-yellow-600 mt-2">${{ rentalPrice }}</p>
        </div>

        <!-- Payment Form -->
        <div v-if="!paymentComplete && !paymentDeclined && stripePromise">
          <Elements :stripe="stripePromise" :options="stripeOptions">
            <CheckoutForm
              :rental-price="rentalPrice"
              :video-id="videoId"
              @payment-complete="handlePaymentComplete"
              @payment-error="handlePaymentError"
            />
          </Elements>
        </div>

        <!-- Declined State -->
        <div v-if="paymentDeclined" class="text-center py-8">
          <font-awesome-icon icon="xmark-circle" class="text-4xl text-red-500 mb-4" />
          <h3 class="text-xl font-bold text-gray-900 mb-2">Payment Declined!</h3>
          <p class="text-gray-600 mb-4">{{ paymentDeclinedMessage }}</p>
          <button @click="closeModal" class="bg-yellow-500 text-black px-4 py-2 rounded-md hover:bg-yellow-400 transition-colors">Close</button>
        </div>


        <!-- Success State -->
        <div v-if="paymentComplete" class="text-center py-8">
          <font-awesome-icon  icon="check-circle" class="text-4xl text-green-500 mb-4" />
          <h3 class="text-xl font-bold text-gray-900 mb-2">Payment Successful!</h3>
          <p class="text-gray-600 mb-4">You can now watch this video for the next 72 hours.</p>
          <p class="text-gray-600 mb-4">This popup will close automatically in 3 seconds.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { Elements } from 'vue-stripe'
import { loadStripe } from '@stripe/stripe-js'
import CheckoutForm from './CheckoutForm.vue'
import { useAuth } from '@/composables/useAuth'
import { generateSlug } from '@/utils/slugUtils'

// Props
const props = defineProps({
  videoTitle: {
    type: String,
    required: true
  },
  videoId: {
    type: String,
    required: true
  },
  rentalPrice: {
    type: [String, Number],
    default: '4.99'
  },
  buttonClass: {
    type: String,
    default: ''
  }
})

// Emits
const emit = defineEmits(['rental-success'])

// Reactive data
const showModal = ref(false)
const paymentComplete = ref(false)
const paymentDeclined = ref(false)
const paymentDeclinedMessage = ref('')

const { isAuthenticated, login } = useAuth()
const route = useRoute()

// Stripe instance for Elements wrapper
const stripePromise = ref(null)

// Stripe options
const stripeOptions = computed(() => ({
  mode: 'payment',
  amount: Math.round(parseFloat(props.rentalPrice) * 100), // Convert to cents
  currency: 'usd',
  appearance: {
    theme: 'stripe',
    variables: {
      colorPrimary: '#eab308', // yellow-500
      colorBackground: '#ffffff',
      colorText: '#374151',
      colorDanger: '#dc2626',
      fontFamily: 'system-ui, sans-serif',
      spacingUnit: '4px',
      borderRadius: '6px'
    }
  }
}))



// Methods
const initializeStripe = async () => {
  try {
    const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
  
    if (!stripePublishableKey) {
      throw new Error('Stripe publishable key is not set')
    }

    stripePromise.value = loadStripe(stripePublishableKey)
  } catch (error) {
    console.error('Error initializing Stripe:', error)
    errorMessage.value = 'Failed to initialize payment system. Please try again later.'
  }
}

const handleClick = () => {
  if (isAuthenticated.value) {
    showModal.value = true
  } else {
    const slug = generateSlug(props.videoTitle, props.videoId)
    const returnTo = `/video/${slug}`
    login({returnTo, partnerId: route.params.partnerId})
  }
}

const closeModal = () => {
  showModal.value = false
  resetForm()
}

const resetForm = () => {
  paymentComplete.value = false
  paymentDeclined.value = false
  paymentDeclinedMessage.value = ''
}

const handlePaymentComplete = (paymentData) => {
  if (paymentData.paymentIntent.status === 'succeeded') {
    paymentComplete.value = true
    emit('rental-success', paymentData)
  } else {
    paymentComplete.value = false
    paymentDeclined.value = true
    paymentDeclinedMessage.value = paymentData?.paymentIntent?.last_payment_error?.message || 'Payment declined. Please try again.'
  }
}

const handlePaymentError = (error) => {
  console.error('Payment error:', error)
  // Error handling is done in CheckoutForm
}

// Lifecycle hooks
onMounted(async () => {
  await initializeStripe()
})
</script>

<style scoped>
/* Additional styles for Stripe elements if needed */
.StripeElement {
  height: 40px;
  padding: 10px 12px;
  width: 100%;
  color: #32325d;
  background-color: white;
  border: 1px solid transparent;
  border-radius: 4px;
  box-shadow: 0 1px 3px 0 #e6ebf1;
  -webkit-transition: box-shadow 150ms ease;
  transition: box-shadow 150ms ease;
}

.StripeElement--focus {
  box-shadow: 0 1px 3px 0 #cfd7df;
}

.StripeElement--invalid {
  border-color: #fa755a;
}

.StripeElement--webkit-autofill {
  background-color: #fefde5 !important;
}
</style>
